import { useTranslation } from "react-i18next";
import {
    Alert,
    Box,
    CircularProgress,
    IconButton,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { Icon } from "@tribe/react-mui-overheid-thema";
import { useState } from "react";
import { usePermissions } from "@/src/lib/usePermissions/usePermissions";

type Provider = {
    id: string;
    name: string;
    cocNumber: string;
    did: string;
};

interface ProvidersTableProps {
    providers: Provider[];
    loading: boolean;
    onDeleteClick: (providerID: string) => any;
    page: number;
    rowsPerPage: number;
    totalCount: number;
    onPageChange: (event: unknown, newPage: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const HealthcareProvidersTable = ({
    providers,
    loading,
    onDeleteClick,
    page,
    rowsPerPage,
    totalCount,
    onPageChange,
    onRowsPerPageChange,
}: ProvidersTableProps) => {
    const { t } = useTranslation("common", {
        keyPrefix: "healthcare-provider-excerpts-overview",
    });

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarKey, setSnackbarKey] = useState(0);

    const handleCopyDid = (did: string) => {
        if (!did) return;

        navigator.clipboard.writeText(did);
        setSnackbarKey((prev) => prev + 1);
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = (
        _event: React.SyntheticEvent | Event,
        reason?: string
    ) => {
        if (reason === "clickaway") {
            return;
        }

        setSnackbarOpen(false);
    };

    const { hasPermission } = usePermissions();

    return (
        <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="providers table">
                <TableHead>
                    <TableRow>
                        <TableCell>{t("table.name")}</TableCell>
                        <TableCell>{t("table.cocNumber")}</TableCell>
                        <TableCell>{t("table.did")}</TableCell>
                        <TableCell>{t("table.delete")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell colSpan={4} align="center">
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        p: 2,
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            </TableCell>
                        </TableRow>
                    ) : providers?.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={2} align="center">
                                {t("table.no-data")}
                            </TableCell>
                        </TableRow>
                    ) : (
                        providers?.map((provider) => (
                            <TableRow
                                key={provider.id}
                                sx={{
                                    "&:last-child td, &:last-child th": {
                                        border: 0,
                                    },
                                    cursor: "pointer",
                                    "&:hover": {
                                        backgroundColor: "action.hover",
                                    },
                                }}
                            >
                                <TableCell component="th" scope="row">
                                    {provider.name}
                                </TableCell>
                                <TableCell>{provider.cocNumber}</TableCell>
                                <TableCell
                                    sx={{
                                        maxWidth: 200,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        height: 77,
                                    }}
                                    onClick={() => {
                                        handleCopyDid(provider.did);
                                    }}
                                >
                                    <Tooltip
                                        title={
                                            <>
                                                <Typography>
                                                    {t(
                                                        "table.click-to-copy-did"
                                                    )}
                                                </Typography>
                                                {provider.did ?? "-"}
                                            </>
                                        }
                                    >
                                        <Box
                                            component="span"
                                            sx={{
                                                width: "100%",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {provider.did ?? "-"}
                                        </Box>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="center">
                                    {hasPermission(
                                        "HEALTHCARE_PROVIDER_EXCERPT_CREDENTIAL_REVOKE"
                                    ) ? (
                                        <IconButton
                                            edge="end"
                                            onClick={() =>
                                                onDeleteClick(provider.id)
                                            }
                                            aria-label={t("table.delete")}
                                            data-item-id={provider.id}
                                        >
                                            <Icon name="verwijderen" />
                                        </IconButton>
                                    ) : (
                                        "-"
                                    )}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
            <TablePagination
                rowsPerPageOptions={[5, 15, 25]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />
            <Snackbar
                key={snackbarKey}
                open={snackbarOpen}
                onClose={handleCloseSnackbar}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="success" onClose={handleCloseSnackbar}>
                    {t("table.did-copied")}
                </Alert>
            </Snackbar>
        </TableContainer>
    );
};

export default HealthcareProvidersTable;
